import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, fmtNum, PHASE_COLORS, PHASE_LABELS, PHASES_ORDER } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Check, ChevronDown, Clock, Euro, Calendar, BarChart3 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';
import { toast } from 'sonner';

const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i);

// Palette de 12 couleurs distinctes pour les courbes projets
const PALETTE = [
  '#002FA7', '#dc2626', '#059669', '#f59e0b', '#7c3aed', '#0891b2',
  '#be185d', '#65a30d', '#ea580c', '#0284c7', '#7e22ce', '#a16207',
];

// ─────────────────────────────────────────────────────────────────────
//  MultiSelect : composant générique pour sélection multiple
// ─────────────────────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, allLabel = 'Tous', testid, searchable = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const allSelected = selected.length === 0;
  const summary = allSelected
    ? allLabel
    : selected.length === 1
    ? options.find(o => o.value === selected[0])?.label || '1 sélectionné'
    : `${selected.length} sélectionnés`;

  const filtered = searchable && search.trim()
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val];
    onChange(next);
  };

  return (
    <div className="min-w-[180px]">
      <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="w-full mt-1 px-3 py-2 border border-zinc-300 bg-white text-sm text-left flex items-center justify-between hover:border-zinc-900 transition-colors"
            data-testid={testid}
          >
            <span className={allSelected ? 'text-zinc-500' : 'font-medium'}>{summary}</span>
            <ChevronDown size={14} className="text-zinc-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="rounded-none p-0 w-[320px] max-h-[400px] overflow-y-auto" align="start">
          {searchable && (
            <div className="border-b border-zinc-200 p-2 sticky top-0 bg-white z-10">
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full px-2 py-1.5 text-sm border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                data-testid={`${testid}-search`}
              />
            </div>
          )}
          <div className="border-b border-zinc-200 px-3 py-2 flex items-center justify-between">
            <button
              onClick={() => { onChange([]); setSearch(''); }}
              className="text-xs text-zinc-600 hover:text-zinc-900 underline"
              data-testid={`${testid}-clear`}
            >
              Effacer
            </button>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{selected.length}/{options.length}{search && ` (${filtered.length} filtrés)`}</span>
          </div>
          {filtered.map(o => {
            const isOn = selected.includes(o.value);
            return (
              <button
                key={o.value}
                onClick={() => toggle(o.value)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-zinc-50 ${isOn ? 'bg-blue-50' : ''}`}
                data-testid={`${testid}-opt-${o.value}`}
              >
                <span className={`w-4 h-4 border ${isOn ? 'bg-[#002FA7] border-[#002FA7]' : 'border-zinc-400'} flex items-center justify-center`}>
                  {isOn && <Check size={10} className="text-white" />}
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-3 py-4 text-xs text-zinc-500 text-center">Aucun résultat</div>}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function AnalyseTemporelle() {
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [moisDebut, setMoisDebut] = useState('1');
  const [moisFin, setMoisFin] = useState('12');
  const [promoteurIds, setPromoteurIds] = useState([]);
  const [projetIds, setProjetIds] = useState([]);
  const [salarieIds, setSalarieIds] = useState([]);
  const [metric, setMetric] = useState('heures'); // 'heures' | 'euros'
  const [granularite, setGranularite] = useState('mois'); // 'mois' | 'jour'

  // Data
  const [data, setData] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null); // { period, breakdown, ... }

  useEffect(() => {
    (async () => {
      const [p, c, u] = await Promise.all([
        api.get('/projets'),
        api.get('/promoteurs'),
        api.get('/users'),
      ]);
      setProjets(p.data);
      setClients(c.data);
      setSalaries(u.data);
    })();
  }, []);

  // Filtrage projets dispo selon les promoteurs sélectionnés
  const projetsAffiches = useMemo(() => {
    if (promoteurIds.length === 0) return projets;
    return projets.filter(p => promoteurIds.includes(p.promoteur_id));
  }, [projets, promoteurIds]);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    setSelectedPoint(null);
    try {
      const params = new URLSearchParams();
      if (annee !== 'all') params.set('annee', annee);
      params.set('mois_debut', moisDebut);
      params.set('mois_fin', moisFin);
      params.set('granularite', granularite);
      if (promoteurIds.length) params.set('promoteur_ids', promoteurIds.join(','));
      if (projetIds.length) params.set('projet_ids', projetIds.join(','));
      if (salarieIds.length) params.set('salarie_ids', salarieIds.join(','));
      const { data: d } = await api.get(`/analytics/timeline?${params}`);
      setData(d);
    } catch (err) {
      console.error('[AnalyseTemporelle] load failed', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur chargement données');
    } finally {
      setLoading(false);
    }
  }, [annee, moisDebut, moisFin, granularite, promoteurIds, projetIds, salarieIds]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // Vérification 3 mois max si jour
  useEffect(() => {
    if (granularite === 'jour') {
      const start = Number(moisDebut), end = Number(moisFin);
      if (end - start > 2) {
        toast.warning('Détail jour : période réduite à 3 mois max.');
        setMoisFin(String(Math.min(12, start + 2)));
      }
    }
  }, [granularite]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────
  // Préparation données pour Recharts : matrice pivotée par période × projet
  // ─────────────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!data?.series) return [];
    const periodSet = new Set();
    for (const s of data.series) for (const p of s.points) periodSet.add(p.period);
    const sortedPeriods = Array.from(periodSet).sort();
    return sortedPeriods.map(period => {
      const row = { period, label: granularite === 'mois' ? formatMonthLabel(period) : period.slice(5) };
      for (const s of data.series) {
        const pt = s.points.find(p => p.period === period);
        row[s.projet_code] = pt ? Math.round((metric === 'heures' ? pt.heures : pt.euros) * 100) / 100 : 0;
      }
      return row;
    });
  }, [data, metric, granularite]);

  // Click sur un point → on extrait la breakdown salariés pour la période
  const handleChartClick = (e) => {
    if (!e || !e.activeLabel || !data?.series) return;
    const periodLabel = e.activeLabel;
    // retrouver la période originale via chartData
    const row = chartData.find(r => r.label === periodLabel);
    if (!row) return;
    const period = row.period;
    // Agréger salariés sur la période
    const agg = {};
    for (const s of data.series) {
      const pt = s.points.find(p => p.period === period);
      if (!pt) continue;
      for (const b of pt.breakdown) {
        if (!agg[b.salarie_id]) agg[b.salarie_id] = { salarie_nom: b.salarie_nom, heures: 0, euros: 0, projets: [] };
        agg[b.salarie_id].heures += b.heures;
        agg[b.salarie_id].euros += b.euros;
        agg[b.salarie_id].projets.push({ projet_code: s.projet_code, projet_nom: s.projet_nom, heures: b.heures, euros: b.euros });
      }
    }
    const breakdown = Object.values(agg).sort((a, b) => b.heures - a.heures);
    setSelectedPoint({ period, label: periodLabel, breakdown });
  };

  const totalLabel = metric === 'heures'
    ? `${fmtNum(data?.total?.heures || 0, 1)} h`
    : fmtEUR(data?.total?.euros || 0);

  return (
    <Layout
      title="Analyse temporelle"
      subtitle="Suivi des heures et coûts par projet, mois par mois"
    >
      {/* ───────────── Filtres ───────────── */}
      <div className="bg-white border border-zinc-200 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Année */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Année</Label>
            <Select value={annee} onValueChange={setAnnee}>
              <SelectTrigger className="rounded-none w-36" data-testid="filter-annee"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Toutes années</SelectItem>
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Mois début / fin */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">De</Label>
            <Select value={moisDebut} onValueChange={setMoisDebut}>
              <SelectTrigger className="rounded-none w-24" data-testid="filter-mois-debut"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {months.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">À</Label>
            <Select value={moisFin} onValueChange={setMoisFin}>
              <SelectTrigger className="rounded-none w-24" data-testid="filter-mois-fin"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {months.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Multi-sélection promoteurs */}
          <MultiSelect
            label="Promoteurs"
            options={clients.map(c => ({ value: c.id, label: c.nom }))}
            selected={promoteurIds}
            onChange={setPromoteurIds}
            allLabel="Tous les promoteurs"
            testid="filter-promoteurs"
          />

          {/* Multi-sélection projets */}
          <MultiSelect
            label="Projets"
            options={projetsAffiches.map(p => ({ value: p.id, label: `${p.code} — ${p.nom}` }))}
            selected={projetIds}
            onChange={setProjetIds}
            allLabel="Tous les projets"
            testid="filter-projets"
            searchable
          />

          {/* Multi-sélection salariés */}
          <MultiSelect
            label="Salariés"
            options={salaries.map(s => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))}
            selected={salarieIds}
            onChange={setSalarieIds}
            allLabel="Tous les salariés"
            testid="filter-salaries"
            searchable
          />
        </div>

        {/* Toggles : heures/euros + mois/jour */}
        <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-zinc-100">
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 mr-2">Métrique</Label>
            <div className="flex border border-zinc-300">
              <button
                onClick={() => setMetric('heures')}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 transition-colors ${metric === 'heures' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid="toggle-heures"
              >
                <Clock size={12} /> Heures
              </button>
              <button
                onClick={() => setMetric('euros')}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 border-l border-zinc-300 transition-colors ${metric === 'euros' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid="toggle-euros"
              >
                <Euro size={12} /> Euros
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 mr-2">Détail</Label>
            <div className="flex border border-zinc-300">
              <button
                onClick={() => setGranularite('mois')}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 transition-colors ${granularite === 'mois' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid="toggle-mois"
              >
                <BarChart3 size={12} /> Mois
              </button>
              <button
                onClick={() => setGranularite('jour')}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 border-l border-zinc-300 transition-colors ${granularite === 'jour' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid="toggle-jour"
                title="Plage limitée à 3 mois maximum"
              >
                <Calendar size={12} /> Jour
              </button>
            </div>
            {granularite === 'jour' && <span className="text-[10px] text-amber-600 ml-1">⚠ 3 mois max</span>}
          </div>

          <div className="ml-auto flex items-end gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Total période</div>
              <div className="font-mono font-medium text-2xl" data-testid="analyse-total">{totalLabel}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── Graphique principal ───────────── */}
      <div className="bg-white border border-zinc-200 mb-6">
        <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">
            Courbe temporelle — {metric === 'heures' ? 'Heures' : 'Coût (€)'} par {granularite === 'mois' ? 'mois' : 'jour'}
          </div>
          <div className="text-[10px] text-zinc-500">{data?.series?.length || 0} projet(s) — Cliquez sur un point pour voir le détail salariés</div>
        </div>
        <div className="p-4">
          {loading && <div className="h-[420px] flex items-center justify-center text-zinc-400 text-sm">Chargement…</div>}
          {!loading && chartData.length === 0 && (
            <div className="h-[420px] flex items-center justify-center text-zinc-400 text-sm">Aucune donnée pour cette période et ces filtres.</div>
          )}
          {!loading && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={chartData} onClick={handleChartClick} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'euros' ? `${Math.round(v / 1000)}k` : v} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #18181b', borderRadius: 0, fontSize: 12 }}
                  formatter={(v, name) => metric === 'euros' ? [fmtEUR(v), name] : [`${fmtNum(v, 1)} h`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {(data?.series || []).map((s, idx) => (
                  <Line
                    key={s.projet_id}
                    type="monotone"
                    dataKey={s.projet_code}
                    name={`${s.projet_code} — ${s.projet_nom.slice(0, 25)}`}
                    stroke={PALETTE[idx % PALETTE.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6, cursor: 'pointer' }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ───────────── Détail au clic : breakdown salariés ───────────── */}
      {selectedPoint && (
        <div className="bg-white border-2 border-[#002FA7] mb-6" data-testid="point-detail">
          <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between bg-blue-50">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-medium">Détail — Période {selectedPoint.label}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{selectedPoint.breakdown.length} salarié(s) ont pointé sur cette période</div>
            </div>
            <button onClick={() => setSelectedPoint(null)} className="text-zinc-500 hover:text-zinc-900 text-sm">✕ Fermer</button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Salarié</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projets imputés</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Heures</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Coût</th>
                </tr>
              </thead>
              <tbody>
                {selectedPoint.breakdown.map(b => (
                  <tr key={b.salarie_nom} className="border-b border-zinc-100">
                    <td className="px-4 py-2 font-medium">{b.salarie_nom}</td>
                    <td className="px-4 py-2 text-xs text-zinc-600">
                      {b.projets.slice(0, 3).map(p => (
                        <div key={p.projet_code}><span className="font-mono text-zinc-500">{p.projet_code}</span> · {fmtNum(p.heures, 1)}h</div>
                      ))}
                      {b.projets.length > 3 && <div className="text-zinc-400">+ {b.projets.length - 3} autre(s)</div>}
                    </td>
                    <td className="px-4 py-2 font-mono text-right">{fmtNum(b.heures, 1)} h</td>
                    <td className="px-4 py-2 font-mono text-right text-xs">{fmtEUR(b.euros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────────── Graphique 2 — Par promoteur OU par mois (si 1 projet) ───────────── */}
      {!loading && data && (
        <div className="bg-white border border-zinc-200 mb-6">
          <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">
              {projetIds.length === 1
                ? `Phases du projet — Détail mensuel`
                : `Ventilation par promoteur — Phases d'imputation`
              }
            </div>
            <div className="text-[10px] text-zinc-500 flex items-center gap-3">
              {PHASES_ORDER.map(ph => (
                <span key={ph} className="flex items-center gap-1">
                  <span className="w-3 h-3 inline-block" style={{ background: PHASE_COLORS[ph] }} />
                  {PHASE_LABELS[ph]}
                </span>
              ))}
            </div>
          </div>
          <div className="p-4">
            {(() => {
              const isSingleProject = projetIds.length === 1;
              const rows = isSingleProject ? (data.by_period_phase || []) : (data.by_promoteur_phase || []);
              if (rows.length === 0) {
                return <div className="h-[300px] flex items-center justify-center text-zinc-400 text-sm">Aucune donnée.</div>;
              }
              // Build flat chart data
              const chartRows = rows.map(r => {
                const row = {
                  label: isSingleProject ? formatMonthLabel(r.period) : (r.promoteur_nom || 'Sans promoteur'),
                };
                for (const ph of PHASES_ORDER) {
                  row[ph] = Math.round(((r.phases?.[ph]?.[metric]) || 0) * 100) / 100;
                }
                row._total = r.total?.[metric] || 0;
                return row;
              });
              return (
                <ResponsiveContainer width="100%" height={Math.max(320, chartRows.length * 28 + 80)}>
                  <BarChart
                    data={chartRows}
                    layout={isSingleProject ? 'horizontal' : 'vertical'}
                    margin={{ top: 10, right: 20, bottom: 10, left: isSingleProject ? 10 : 120 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    {isSingleProject ? (
                      <>
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'euros' ? `${Math.round(v / 1000)}k` : v} />
                      </>
                    ) : (
                      <>
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'euros' ? `${Math.round(v / 1000)}k` : v} />
                        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={120} />
                      </>
                    )}
                    <Tooltip
                      contentStyle={{ background: 'white', border: '1px solid #18181b', borderRadius: 0, fontSize: 12 }}
                      formatter={(v, name) => {
                        const lbl = PHASE_LABELS[name] || name;
                        return metric === 'euros' ? [fmtEUR(v), lbl] : [`${fmtNum(v, 1)} h`, lbl];
                      }}
                    />
                    {PHASES_ORDER.map(ph => (
                      <Bar key={ph} dataKey={ph} stackId="phase" fill={PHASE_COLORS[ph]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
          {/* Récap heures à risque vs PC vs post-PC, uniquement si 1 projet */}
          {projetIds.length === 1 && data.by_period_phase?.length > 0 && (() => {
            const totals = { a_risque: { h: 0, e: 0 }, pc: { h: 0, e: 0 }, post_pc: { h: 0, e: 0 } };
            for (const row of data.by_period_phase) {
              for (const [ph, vals] of Object.entries(row.phases || {})) {
                if (ph === 'etude_faisabilite' || ph === 'faisabilite_ok') {
                  totals.a_risque.h += vals.heures; totals.a_risque.e += vals.euros;
                } else if (ph === 'projet_conception' || ph === 'pc_depose') {
                  totals.pc.h += vals.heures; totals.pc.e += vals.euros;
                } else if (ph === 'projet_accorde') {
                  totals.post_pc.h += vals.heures; totals.post_pc.e += vals.euros;
                }
              }
            }
            const fmt = (v) => metric === 'euros' ? fmtEUR(v) : `${fmtNum(v, 1)} h`;
            const fld = metric === 'euros' ? 'e' : 'h';
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border-t border-zinc-100" data-testid="phase-risk-recap">
                <div className="bg-rose-50 border border-rose-300 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-medium">À risque (Étude faisa)</div>
                  <div className="font-mono font-medium text-2xl text-rose-700 mt-1">{fmt(totals.a_risque[fld])}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">Heures non rémunérées si projet KO</div>
                </div>
                <div className="bg-blue-50 border border-[#002FA7]/30 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-medium">PC (Conception + Dépôt)</div>
                  <div className="font-mono font-medium text-2xl text-[#002FA7] mt-1">{fmt(totals.pc[fld])}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">Heures pour préparer & déposer le permis</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-400 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-medium">Post-PC (Modificatif)</div>
                  <div className="font-mono font-medium text-2xl text-emerald-700 mt-1">{fmt(totals.post_pc[fld])}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">Sans rémunération supplémentaire en général</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ───────────── Récap par projet ───────────── */}
      {!loading && data?.series?.length > 0 && (
        <div className="bg-white border border-zinc-200">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Récap par projet sur la période</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Phase actuelle</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Heures totales</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Coût total</th>
                </tr>
              </thead>
              <tbody>
                {data.series.map((s, idx) => {
                  const h = s.points.reduce((acc, p) => acc + p.heures, 0);
                  const e = s.points.reduce((acc, p) => acc + p.euros, 0);
                  const phaseColor = s.phase_actuelle ? PHASE_COLORS[s.phase_actuelle] : '#71717a';
                  return (
                    <tr key={s.projet_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 inline-block" style={{ background: PALETTE[idx % PALETTE.length] }} />
                          <div>
                            <div className="font-medium text-xs">{s.projet_nom}</div>
                            <div className="font-mono text-[10px] text-zinc-500">{s.projet_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {s.phase_actuelle ? (
                          <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border" style={{ borderColor: phaseColor, color: phaseColor }}>
                            {PHASE_LABELS[s.phase_actuelle] || s.phase_actuelle}
                          </span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-right">{fmtNum(h, 1)} h</td>
                      <td className="px-4 py-2 font-mono text-right text-xs">{fmtEUR(e)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}

function formatMonthLabel(period) {
  // period = 'YYYY-MM' → 'Janv 25'
  const [y, m] = period.split('-');
  const idx = Number(m) - 1;
  return `${months[idx]} ${y.slice(2)}`;
}
